import React, { ReactNode } from 'react';
import {Text, Button} from 'components/core';
import {Dialog as MuiDialog, Divider, IconButton, Grid, CircularProgress} from '@material-ui/core';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

const overrideStyle = makeStyles((theme) => ({
    root:
    {
      maxWidth: '80vw',
      maxHeight: '90vh'
    },
    title: {
        margin: 0,
        padding: '24px 56px',
        color: theme.palette.text.secondary,
      },
      subtitle:{
        fontSize: '14px'
      },
      closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.text.secondary,
      },
      content: {
        padding: '24px 56px',
      },
      actions: {
        margin: 0,
        padding: '12px 24px'
      },
      actionButton: {
          minWidth: 150
      },
      buttonProgress: {
        color: theme.palette.primary.main,
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -12,
        marginLeft: -12,
      },
  }), {name: "RaymanDialog"});

interface DialogTitleProps {
  children: ReactNode,
  onClose?: () => void,
  subtitle?: string | ReactNode
}

function DialogTitle(props : DialogTitleProps) {
    const classes = overrideStyle();
    const { children, onClose, subtitle, ...other } = props;
    return (
      <MuiDialogTitle disableTypography className={classes.title} {...other}>
          <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        <Text variant="h5">{children}</Text>
        {subtitle ? 
          typeof(subtitle) === "string" ? <Text className={classes.subtitle}>{subtitle}</Text> :  subtitle
        : null }
      </MuiDialogTitle>
    );
  }

interface DialogProps extends DialogTitleProps
{
  open: boolean,
  title?: string,
  confirm?: string | null,
  cancel?: string | null,
  loading?: boolean,
  onClose?: () => void,
  onCancel?: (() => void) | null,
  onConfirm: () => void,
  headerDivider?: boolean,
  confirmColor? : string,
  className? : string,
  contentClass? : string
}
function Dialog({open = false, title = "", subtitle = "", confirm = null, cancel = null, loading = false, onClose = () => {}, onCancel = null, onConfirm = () => {}, headerDivider = false, confirmColor = "primary", ...props} : DialogProps)
{
    const classes = overrideStyle();

    const handleClose = () => {
        onClose();
    };

    const handleConfirm = () => {
        onConfirm();
    }

    const handleCancel = () => {
      if(onCancel !== null) onCancel();
      else onClose();
    }
    const justify = cancel && confirm ? "space-between" : cancel ? "flex-start" : "flex-end";

    const actions = <><Divider /><MuiDialogActions className={classes.actions}>
        <Grid container justify={justify}>
            {cancel ? 
            <Grid item><Button className={classes.actionButton} autoFocus onClick={handleCancel} color="secondary" fullWidth={false}>
                {cancel}
            </Button></Grid>
            : null }
            {confirm ? 
            <Grid item><Button className={classes.actionButton} autoFocus onClick={handleConfirm} color={confirmColor} fullWidth={false} disabled={loading}>
                {confirm}
                {loading && <CircularProgress size={24} className={classes.buttonProgress} />}
            </Button>
            </Grid>
            : null }
        </Grid>
    </MuiDialogActions></>

    return <MuiDialog onClose={handleClose} aria-labelledby="customized-dialog-title" open={open} PaperProps={{className: clsx(classes.root, props.className)}}>
        <DialogTitle subtitle={subtitle} onClose={handleClose}>
          {title}
        </DialogTitle>
        {headerDivider && <Divider />}
        <MuiDialogContent className={clsx(classes.content, props.contentClass)}>
          {props.children}
        </MuiDialogContent>
        { confirm || cancel ? actions : null}
    </MuiDialog>
}

export default Dialog;